import { getSystemTypeIdByAssetTypePrefix } from '@innovyze/lib_asset_schema';
import type { UnitsSystem } from '@innovyze/stylovyze';
import { GRADE_ORDER } from '@Components/RiskDetails/Datagrid/gradeConfig';
import {
	GradeKey,
	LabelBreakdown,
	RiskResultsSummary,
	UNKNOWN_LABEL,
} from '@Types/riskResultsSummary.types';

export interface GradeCardModel {
	grade: GradeKey;
	count: number;
	lengthMetres: number;
	/** Share of scored assets, 0..1. Unscored assets are not in the denominator. */
	share: number;
}

export const buildGradeCards = (
	summary: RiskResultsSummary,
): GradeCardModel[] => {
	const grades = GRADE_ORDER as GradeKey[];
	const scored = grades.reduce(
		(sum, g) => sum + (summary.totals[g]?.count ?? 0),
		0,
	);
	return grades.map(grade => {
		const count = summary.totals[grade]?.count ?? 0;
		return {
			grade,
			count,
			lengthMetres: summary.totals[grade]?.length ?? 0,
			share: scored === 0 ? 0 : count / scored,
		};
	});
};

export const formatShare = (share: number): string =>
	`${Math.round(share * 100)}%`;

// stylovyze's useUnits().getSystemValueFormatted, passed in so this stays a
// pure function that unit tests can drive without the stylovyze bundle.
export type SystemValueFormatter = (
	value: string,
	invalidValue?: string,
	decimalPlaces?: number,
	standardUnit?: string,
) => string | undefined;

// Long-distance unit for network totals: km for Metric, mi for Imperial.
// stylovyze's own metre/foot output reads poorly at network scale. Switch once
// the total reaches one long unit, so it never reads as a fraction of a mile.
const METRES_PER_KILOMETRE = 1000;
const METRES_PER_MILE = 1609.344;
// Two decimals for totals: 1505.55 m reads as 1.51 km, 3.59 m stays 3.59 m.
// Fixed rather than read from lib-asset-schema: its `precision` is per schema
// field and only reachable through the async getAssetSchema, which this
// synchronous display path cannot wait on.
const RESULTS_LENGTH_DECIMALS = 2;

/**
 * Format a length in metres for the Results tab (grade cards and breakdown).
 *
 * Passes metres to stylovyze and lets it convert. Totals of at least 1 km
 * (Metric) or 1 mi (Imperial) are shown in that unit via stylovyze's target-unit
 * argument; smaller totals keep stylovyze's default unit (m or ft), so they are
 * not rounded to 0.00 of a long unit.
 */
export const formatResultsLength = (
	metres: number,
	system: UnitsSystem,
	format: SystemValueFormatter,
): string => {
	const imperial = system === 'Imperial';
	const longUnit = imperial ? 'mi' : 'km';
	const metresPerLongUnit = imperial ? METRES_PER_MILE : METRES_PER_KILOMETRE;
	// Compare the value as it would display (2 dp), so 999.999 m reads 1 km
	// rather than 1000 m.
	const factor = Math.pow(10, RESULTS_LENGTH_DECIMALS);
	const inLongUnit =
		Math.round((metres / metresPerLongUnit) * factor) / factor;
	const standardUnit = inLongUnit >= 1 ? longUnit : undefined;
	return (
		format(`${metres}m`, '-', RESULTS_LENGTH_DECIMALS, standardUnit) ?? '-'
	);
};

// Literal t() calls so the i18next parser extracts them; GRADE_CONFIG.label
// is untranslated English.
export const gradeLabel = (
	t: (key: string) => string,
	grade: GradeKey,
): string => {
	switch (grade) {
		case '0':
			return t('Optimal');
		case '1':
			return t('Stable');
		case '2':
			return t('Moderate');
		case '3':
			return t('Concerning');
		case '4':
			return t('Critical');
	}
};

export interface SliceModel {
	label: string;
	count: number;
	length: number;
	isUnknown: boolean;
}

export const orderSlices = (
	breakdown: LabelBreakdown,
	mode: 'byCount' | 'numericLabel',
): SliceModel[] => {
	const slices: SliceModel[] = Object.keys(breakdown)
		.filter(label => breakdown[label].count > 0)
		.map(label => ({
			label,
			count: breakdown[label].count,
			length: breakdown[label].length,
			isUnknown: label === UNKNOWN_LABEL,
		}));
	return slices.sort((a, b) => {
		if (a.isUnknown !== b.isUnknown) return a.isUnknown ? 1 : -1;
		if (mode === 'numericLabel')
			return Number.parseFloat(a.label) - Number.parseFloat(b.label);
		return b.count - a.count || a.label.localeCompare(b.label);
	});
};

export const buildYearSeries = (
	breakdown: LabelBreakdown,
): { points: [number, number][]; unknownCount: number } => {
	let unknownCount = 0;
	const points: [number, number][] = [];
	Object.keys(breakdown).forEach(label => {
		const year = Number(label);
		const { count } = breakdown[label];
		if (label === UNKNOWN_LABEL || !Number.isInteger(year)) {
			unknownCount += count;
		} else if (count > 0) {
			points.push([year, count]);
		}
	});
	points.sort((a, b) => a[0] - b[0]);
	return { points, unknownCount };
};

// By system type, not a hardcoded asset type: with the asset-schema flags on,
// a Water Distribution model can use asset types other than 'pipe'.
export const isWaterNetwork = (assetType: string | undefined): boolean =>
	!!assetType &&
	getSystemTypeIdByAssetTypePrefix(assetType) === 'WaterDistribution';

export const escapeHtml = (text: string): string =>
	text
		.replaceAll('&', '&amp;')
		.replaceAll('<', '&lt;')
		.replaceAll('>', '&gt;')
		.replaceAll('"', '&quot;')
		// Apostrophe as a char code: prettier and the quotes rule disagree on
		// how to write the literal.
		.replaceAll(String.fromCodePoint(39), '&#39;');
